/**
 * 题1 sh2021q24 — 2021 上海中考 Q24「抛物线与等腰直角三角形」。
 *
 * 已知抛物线 y = ax² + c 过 P(3,0)、Q(1,4)。点 A 在直线 PQ 上（第一象限），
 * AB⊥x 轴于 B，以 AB 为斜边在左侧作等腰直角△ABC。
 *
 * 全部由公式推导：a、c 由两点代入解出；直线 PQ 由两点式；C 由「中点 +
 * 左法向 × AB/2」的等腰直角公式；m 由代入抛物线后的二次方程 (2m−1)(m−3)=0。
 */

import {
  add,
  axisOf,
  dist,
  evalLine,
  evalQuad,
  lineThrough,
  mid,
  pt,
  rotate90,
  scale,
  solveQuadratic,
  unit,
} from "../math/func";
import type { Line2, Pt, Quad } from "../math/func";
import type { ProblemDef, SceneDef } from "./types";

/** (1) 将 P、Q 代入 y = ax² + c：9a + c = 0，a + c = 4。 */
const P = pt(3, 0);
const Q = pt(1, 4);
const a = (P.y - Q.y) / (P.x * P.x - Q.x * Q.x); // -1/2
const cTerm = P.y - a * P.x * P.x; // 9/2

/** 抛物线 y = -½x² + 9/2（对称轴为 y 轴）。 */
export const parabola21: Quad = { a, b: 0, c: cTerm };
const axis = axisOf(parabola21);

/** 直线 PQ：y = -2x + 6。 */
export const linePQ21: Line2 = lineThrough(P, Q);

/** 等腰直角△ABC：AB 为斜边，C 在 AB 左侧 —— 中点 + 左法向 × AB/2。 */
function apexLeft(aPt: Pt, bPt: Pt): Pt {
  const up = unit(bPt, aPt) ?? pt(0, 1);
  return add(mid(aPt, bPt), scale(rotate90(up), dist(aPt, bPt) / 2));
}

/* Scene ①：A 与 Q 重合。 */
const A1 = Q;
const B1 = pt(Q.x, 0);
const C1 = apexLeft(A1, B1); // (-1, 2)
const H1 = pt(B1.x, C1.y); // CH⊥AB 于 H(1, 2)
const distToAxis = Math.abs(C1.x - axis); // 1

/* Scene ②：C 落在抛物线上。代入得 (2m−1)(m−3)=0，m=1/2（m=3 时 A=P=B，舍）。 */
const m =
  solveQuadratic(2, -7, 3).find((root) => Math.abs(root - 3) > 1e-9) ?? Number.NaN;
const A2 = pt(m, evalLine(linePQ21, m)); // (1/2, 5)
const B2 = pt(m, 0); // (1/2, 0)
const C2 = apexLeft(A2, B2); // (-2, 5/2)
const H2 = pt(m, C2.y); // (1/2, 5/2)

function triangleSceneElements(aPt: Pt, bPt: Pt, cPt: Pt, hPt: Pt) {
  return {
    segments: [
      { a: aPt, b: bPt, tone: "gold" as const, width: 2.6 },
      { a: bPt, b: cPt, tone: "gold" as const },
      { a: aPt, b: cPt, tone: "gold" as const },
      { a: cPt, b: hPt, tone: "gold" as const, dashed: true },
    ],
    rightAngles: [
      { at: hPt, from: cPt, to: aPt },
      { at: cPt, from: aPt, to: bPt },
    ],
  };
}

const scene1: SceneDef = {
  id: "s1",
  tab: "① A 与 Q 重合",
  caption:
    "A 与 Q(1,4) 重合时 AB=4。等腰直角△ABC 以 AB 为斜边立于左侧，CH⊥AB 于 H，" +
    "CH=AH=BH=2，故 C(-1,2) 到对称轴（y 轴）的距离为 1。",
  view: { xMin: -3, xMax: 4.5, yMin: -1.2, yMax: 5.5 },
  curves: [
    { kind: "quad", id: "parabola", quad: parabola21, tone: "cyan" },
    { kind: "line", id: "pq", line: linePQ21, tone: "dim", dashed: true },
    { kind: "vline", id: "axis", x: axis, tone: "gold", dashed: true },
  ],
  points: [
    { name: "P", at: P, showCoords: true, dx: 10, dy: 16 },
    { name: "A", at: A1, text: "A(Q)", dx: 12, dy: -10 },
    { name: "B", at: B1, dx: 12, dy: 14 },
    { name: "C", at: C1, emphasis: true, showCoords: true, dx: -12, dy: -12 },
    { name: "H", at: H1, dx: 12, dy: 2 },
  ],
  ...triangleSceneElements(A1, B1, C1, H1),
};

const scene2: SceneDef = {
  id: "s2",
  tab: "② C 落在抛物线上",
  caption:
    "设 A(m,-2m+6)，则 C(2m-3,-m+3)。代入抛物线整理得 (2m-1)(m-3)=0，" +
    "m=1/2（m=3 时 A 与 B 重合，舍去），故 C(-2, 5/2)。",
  view: { xMin: -3.5, xMax: 4.5, yMin: -1.2, yMax: 6 },
  curves: [
    { kind: "quad", id: "parabola", quad: parabola21, tone: "cyan" },
    { kind: "line", id: "pq", line: linePQ21, tone: "dim", dashed: true },
    { kind: "vline", id: "axis", x: axis, tone: "gold", dashed: true },
  ],
  points: [
    { name: "P", at: P, showCoords: true, dx: 10, dy: 16 },
    { name: "Q", at: Q, dx: 12, dy: -8 },
    { name: "A", at: A2, showCoords: true, dx: 12, dy: -10 },
    { name: "B", at: B2, dx: 12, dy: 14 },
    { name: "C", at: C2, emphasis: true, showCoords: true, dx: -12, dy: -12 },
    { name: "H", at: H2, dx: 12, dy: 2 },
  ],
  ...triangleSceneElements(A2, B2, C2, H2),
};

export const sh2021q24: ProblemDef = {
  id: "sh2021q24",
  title: "2021·Q24 等腰直角三角形",
  statement:
    "已知抛物线 y=ax²+c（a≠0）过点 P(3,0)、Q(1,4)。（1）求抛物线的解析式；" +
    "（2）点 A 在直线 PQ 上且在第一象限内，过 A 作 AB⊥x 轴于 B，以 AB 为斜边在其左侧作等腰直角△ABC。" +
    "① 若 A 与 Q 重合，求 C 到抛物线对称轴的距离；② 若 C 落在抛物线上，求 C 的坐标。",
  givens: [
    { label: "P", value: "(3, 0)" },
    { label: "Q", value: "(1, 4)" },
    { label: "△ABC", value: "等腰直角，AB 为斜边" },
  ],
  scenes: [scene1, scene2],
  solution: [
    "（1）将 P(3,0)、Q(1,4) 代入 y=ax²+c，得 9a+c=0、a+c=4，解得 a=-1/2，c=9/2。" +
      "抛物线的解析式是 y=-½x²+9/2。",
    "（2）① 抛物线的对称轴是 y 轴。A 与 Q(1,4) 重合时 AB=4。作 CH⊥AB 于 H，" +
      "由等腰直角三角形性质，△CBH 和 △CAH 也是等腰直角三角形，CH=AH=BH=2，" +
      "所以 C 到对称轴的距离等于 1。",
    "② 设直线 PQ：y=kx+b，代入 P、Q 得 k=-2、b=6，即 y=-2x+6。" +
      "设 A(m,-2m+6)，则 CH=BH=AH=-m+3，y_C=-m+3，x_C=2m-3。",
    "将 C(2m-3,-m+3) 代入抛物线：-m+3=-½(2m-3)²+9/2，整理得 (2m-1)(m-3)=0，" +
      "解得 m=1/2 或 m=3（与点 B 重合，舍去）。m=1/2 时 C 的坐标是 (-2, 5/2)。",
  ],
  answer: "① 距离 1 ｜ ② C(-2, 5/2)",
  computed: {
    a,
    c: cTerm,
    lineK: linePQ21.k,
    lineB: linePQ21.b,
    ab1: dist(A1, B1),
    ch1: dist(C1, H1),
    distToAxis,
    m,
    c2x: C2.x,
    c2y: C2.y,
    c2OnParabola: evalQuad(parabola21, C2.x),
  },
};
